import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GetStockSummary } from '../../store/slices/reports';
import { FaFileExcel} from 'react-icons/fa';
import { StockSummaryDownload } from '../../common/ExcelDownload';
import {toast } from 'react-hot-toast';
import Table from '../../common/Table';
import Navbarside from '../Navbarside';
import Loader from '../../common/Loader';
import Footer from '../Footer';

const StockSummary = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tableRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [stockSummary, setStockSummary] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState([]); // Stores selected row IDs
  const [selectAll, setSelectAll] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));
  const Name = user?.data?.company_name;
  const profile_id = user?.data?.profile_id;

  const [columns, setColumns] = useState([
    { header: 'Checkbox', field: 'Checkbox' },
    { header: 'Item Name', field: 'item_name' },
    { header: 'Opening Stock Quantity', field: 'opening_stock_qty' },
    { header: 'Purchased (QTY)', field: 'total_purchased' },
    { header: 'Sold (QTY)', field: 'total_sold' },

    { header: 'Sales Return (QTY)', field: 'credit_note_qty' },
    { header: 'Purchase Return (QTY)', field: 'debit_note_qty' },
    { header: 'Stock Adjustment (QTY)', field: 'stock_adjustment' },
    { header: 'Current Stock', field: 'current_stock' },
    { header: 'Avg Purchase Price', field: 'avg_purchase_price' },
    { header: 'Current Stock Value', field: 'current_stock_value' },
    { 
      header: 'Actions', 
      field: 'actions', 
      isAction: true, 
      actionButtons: [
        { name: 'View Item Ledger', className: 'btn-default' }, 
      ]
    }
  ]);


   // Load saved financial year and dates from localStorage
const loadSavedDates = () => {
  const savedFinancialYear = localStorage.getItem('selectedFinancialYear');
  
  // Ensure financial year is always correctly structured
  let startYear, endYear;
  if (savedFinancialYear && savedFinancialYear.includes('-')) {
    [startYear, endYear] = savedFinancialYear.split('-');
  } else {
    // Default to the current financial year
    const currentYear = new Date().getFullYear();
    startYear = currentYear - 1; // Example: 2024 (for 2024-2025)
    endYear = currentYear;       // Example: 2025 (for 2024-2025)
  }

  return {
    financialYear: savedFinancialYear || `${startYear}-${endYear}`,
    fromDate: localStorage.getItem('savedFromDate') || `${startYear}-04-01`,
    toDate: localStorage.getItem('savedToDate') || `${endYear}-03-31`,
    minDate: `${startYear}-04-01`,
    maxDate: `${endYear}-03-31`,
  };
};


const { fromDate: initialFromDate, toDate: initialToDate, minDate, maxDate, financialYear: initialFinancialYear } = loadSavedDates();

const [fromDate, setFromDate] = useState(initialFromDate);
const [toDate, setToDate] = useState(initialToDate);
const [financialYear, setFinancialYear] = useState(initialFinancialYear);


useEffect(() => {
  if (!financialYear) return;

  localStorage.setItem('selectedFinancialYear', financialYear);

  let startYear, endYear;
  if (financialYear.includes('-')) {
    [startYear, endYear] = financialYear.split('-');
  } else {
    return; // Exit if financial year is not valid
  }

  const savedFromDate = localStorage.getItem('savedFromDate') || `${startYear}-04-01`;
  const savedToDate = localStorage.getItem('savedToDate') || `${endYear}-03-31`;

  setFromDate(savedFromDate);
  setToDate(savedToDate);

  if (!localStorage.getItem('savedFromDate')) localStorage.setItem('savedFromDate', savedFromDate);
  if (!localStorage.getItem('savedToDate')) localStorage.setItem('savedToDate', savedToDate);
}, [financialYear]);


  useEffect(() => {
    handleStockSummary();
  }, [financialYear]); 


  const handleLedger = (item) => {
    navigate(`/item-ledger-list/${item.item_id ? item.item_id : null}`)
    // Implement your edit logic here
};


  const handleStockSummary = async () => {
    const newItem = {
      profile_id: profile_id,
      from_date: fromDate, // Ensure from_date is sent
      to_date: toDate, // Ensure to_date is sent
    };

    setIsLoading(true);

    try {
      const data = await dispatch(GetStockSummary(newItem)).unwrap();

      setStockSummary(data?.stock_report || []); // Update stockSummary with the response
    } catch (error) {
      console.error('Error fetching stock summary:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.trim()); // Trim input to avoid trailing spaces
  };

  // Filter data based on search query
  const filteredStockEntries = (stockSummary || [])?.filter((item) => {
    const search = searchQuery.toLowerCase().trim(); // Trimmed search query
  
    return (
      item?.item_name?.toLowerCase().includes(search) || // Ensure case-insensitive matching
      String(item?.opening_stock_qty || '0').toLowerCase().includes(search) ||
      String(item?.opening_stock_value || '0').toLowerCase().includes(search) ||
      String(item?.total_purchased || '0').toLowerCase().includes(search) ||
      String(item?.total_sold || '0').toLowerCase().includes(search) ||
      String(item?.current_stock || '0').toLowerCase().includes(search) ||
      String(item?.current_stock_value || '0').toLowerCase().includes(search)
    );
  });

  const totalCurrentStockValue = filteredStockEntries?.reduce((acc, row) => {
  const value = parseFloat(row?.current_stock_value) || 0;
  return acc + value;
}, 0);

  const formattedTotalCurrentStockValue = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(totalCurrentStockValue);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };


  const handleFromDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate >= minDate && newDate <= maxDate) {
      setFromDate(newDate);
      localStorage.setItem('savedFromDate', newDate);
    } else {
         toast.error(`Select a date within the financial year ${formatFinancialYear(minDate, maxDate)}`);
    }
  };
  
  const handleToDateChange = (e) => {
    const newDate = e.target.value;
    if (newDate >= minDate && newDate <= maxDate) {
      setToDate(newDate);
      localStorage.setItem('savedToDate', newDate);
    } else {
         toast.error(`Select a date within the financial year ${formatFinancialYear(minDate, maxDate)}`);
    }
  };

  const formatFinancialYear = (startDate, endDate) => {
    return `(${startDate.split("-").reverse().join("-")} to ${endDate.split("-").reverse().join("-")})`;
  };

  const handleExcelDownload = () => {
    const selectedInvoices = selectAll ? stockSummary?.map((invoice) => invoice.item_id) : selectedRows;

    if (!selectedInvoices || selectedInvoices.length === 0) {
      alert('Please select at least one invoice.');
      return;
    }

    const selectedData = stockSummary?.filter((invoice) => selectedInvoices.includes(invoice.item_id));

    if (selectedData.length === 0) {
      alert('No matching invoices found.');
      return;
    }

    try {
      StockSummaryDownload(selectedData, 'StockSummary.xlsx');
      toast.success('Downloading Excel file successfully!');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Failed to download the Excel file. Please try again.');
    }
  };

  return (
    <div>
      <div class="row" style={{ marginLeft: '0', marginRight: '0' }}>
        <Navbarside />
        {isLoading && <Loader />}
        <div className="col-md-10">
          <div className="row top-header">
            <div className="col-md-7">
              <div className="company-name">{Name}</div>
            </div>
            <div className="col-md-5">
              <div className="d-flex justify-content-end">
                <button type="submit" className="btn btn-default" onClick={() => navigate('/ledger')}>
                  Ledger
                </button>
                <button type="submit" className="btn btn-default" onClick={() => navigate('/invoice')}>
                  Sale
                </button>
                <button type="submit" className="btn btn-default">
                  Purchase
                </button>
              </div>
            </div>
          </div>
          <div className="row content-body">
            <div className="container-fluid">
              <div className="page-header">
                <div>
                  <h2 class="main-content-title tx-24 mg-b-5">Stock Summary</h2>
                  <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                      <a href="#">Reports</a>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page">
                      List
                    </li>
                  </ol>
                </div>
               <div className="d-flex justify-content-end">
                            <button className="btn ripple btn-default" onClick={handleExcelDownload}>
                              Export Excel <FaFileExcel />
                            </button>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="row">
                    <div class="col-md-12">
                      <div class="card custom-card">
                        <div class="card-body">
                          <div class="row">
                            <div class="col-md-4 form-inline">
                              <div class="form-group">
                                <label class="">From Date</label>
                                <input class="form-control" type="date" value={fromDate} onChange={handleFromDateChange} min={minDate} max={maxDate}/>
                              </div>
                            </div>
                            <div class="col-md-4 form-inline">
                              <div class="form-group">
                                <label class="">To Date</label>
                                <input class="form-control" type="date" value={toDate} onChange={handleToDateChange} min={minDate} max={maxDate}  />
                              </div>
                            </div>
                            <div class="col-md-3 form-inline">
                              <div class="form-group">
                                <button type="submit" class="btn btn-default"   onClick={() => handleStockSummary()}>
                                  Submit
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-md-12">
                      <div class="row mt-3">
                        <div class="col-md-12">
                        <Table
                            columns={columns}
                            data={filteredStockEntries}
                            tableRef={tableRef}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            currentPage={currentPage}
                            totalCount={filteredStockEntries?.length}
                            onPageChange={handlePageChange}
                            handleLedger ={handleLedger}
                            handleSearchChange={handleSearchChange}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            selectAll={selectAll}
                            setSelectAll={setSelectAll}
                            formattedTotalCurrentStockValue={formattedTotalCurrentStockValue}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default StockSummary;
